import { Dispatch } from "redux";
import { MapStateToPropsFactory, MapDispatchToProps } from "react-redux";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { createSimpleSelectionsCounterSelector } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import {
  createHasMultiplesSelector,
  createQuickBetslipBetPickerSelector,
  QuickBetslipBet,
} from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { BETTING__OBB_UPDATE_QUOTES, BettingObbUpdateQuotesAction } from "@ppb/tbd-store/actions/betting";
import { BetslipStep, ActiveProduct, BetslipSubType } from "@ppb/tbd-store/state/betslip/Betslip.types";
import { BetslipType } from "../../constants";
import {
  getObbReport,
  getSportsbookReport,
  getIsBetslipLoaded,
  getIsReceiptReusePending,
} from "../../store/state/betslip-module/betslip-module-selectors";
import { getActiveBetslipType } from "../../store/state/betslip-type-selectors";
import { getBetslipConfirmation } from "../../store/state/confirmation/confirmation-selectors";
import {
  BetslipCollapseToggleAction,
  BetslipHeaderClickAction,
  UI__BETSLIP_SET_COLLAPSE_ACTION,
  UI__BETSLIP_HEADER_CLICK,
  BetslipCloseAction,
  UI__BETSLIP_CLOSE_CLICK,
} from "@ppb/tbd-store/actions/betslip";
import { getObbBettingPrimeLegs } from "@ppb/tbd-store/state/betting/obb-betting/obb-betting-selectors";
import type { SportsbookBetslipContract } from "../../contract";

export type CardProps = {
  isClosed: boolean;
  step: BetslipStep;
  activeProduct: ActiveProduct;
  activeBetslipType: BetslipType | null;
  isCollapsed: boolean;
  hasMultiples: boolean;
  hasConfirmation: boolean;
  quickBetslipBet: QuickBetslipBet | null;
  isReceiptReusePending: boolean;
};
export type StateProps = CardProps | Record<string, never>;

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const getSportsbookSimpleSelectionsCounter = createSimpleSelectionsCounterSelector();
  const hasMultiplesSelector = createHasMultiplesSelector();
  const getQuickBetslipBetSelector = createQuickBetslipBetPickerSelector();

  return (state: BetslipApplicationState): StateProps => {
    if (!getIsBetslipLoaded(state)) {
      return {};
    }

    const activeBetslipType = getActiveBetslipType(state);

    let totalSelections = 0;
    let hasMultiples = false;
    let hasPlacedCombinations = false;
    const hasObbReports = !!getObbReport(state);

    if (activeBetslipType === BetslipType.OBB) {
      totalSelections = Object.keys(getObbBettingPrimeLegs(state)).length;
    } else {
      hasPlacedCombinations = !!getSportsbookReport(state);
      totalSelections = getSportsbookSimpleSelectionsCounter(state);
      hasMultiples = hasMultiplesSelector(state);
    }

    const isClosed = !hasPlacedCombinations && totalSelections === 0 && !hasObbReports;

    const step = state.betslipModule?.step ?? "PLACE_POTENTIAL";
    const activeProduct = state.betslipModule?.activeProduct ?? "NONE";
    const isCollapsed = state.betslipModule?.isCollapsed ?? false;

    const quickBetslipBet = getQuickBetslipBetSelector(state);

    return {
      quickBetslipBet,
      isReceiptReusePending: getIsReceiptReusePending(state),
      isClosed,
      step,
      activeProduct,
      isCollapsed,
      hasMultiples,
      hasConfirmation: getBetslipConfirmation(state) !== null,
      activeBetslipType,
    };
  };
};

export type DispatchProps = {
  dispatchHeaderToggle: (isCollapsed: boolean, betslipSubType: BetslipSubType, preservePlaceFailures?: boolean) => void;
  dispatchHeaderCollapse: () => void;
  dispatchDismissClick: () => void;
};
export type ContainerProps = {
  contract: SportsbookBetslipContract;
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (
  dispatch: Dispatch<
    BetslipCloseAction | BetslipHeaderClickAction | BetslipCollapseToggleAction | BettingObbUpdateQuotesAction
  >,
) => ({
  dispatchDismissClick: () =>
    dispatch<BetslipCloseAction>({
      type: UI__BETSLIP_CLOSE_CLICK,
    }),
  dispatchHeaderToggle: (isCollapsed: boolean, betslipSubType: BetslipSubType) => {
    dispatch({
      type: UI__BETSLIP_HEADER_CLICK,
      payload: { isCollapsed: !isCollapsed, betslipSubType },
    });
    dispatch({
      type: UI__BETSLIP_SET_COLLAPSE_ACTION,
      payload: {
        collapse: !isCollapsed,
        preservePlaceFailures: !isCollapsed,
      },
    });
    if (isCollapsed) {
      dispatch<BettingObbUpdateQuotesAction>({
        type: BETTING__OBB_UPDATE_QUOTES,
      });
    }
  },
  dispatchHeaderCollapse: () => {
    dispatch({
      type: UI__BETSLIP_SET_COLLAPSE_ACTION,
      payload: {
        collapse: true,
      },
    });
  },
});
